import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowDown, FaArrowUp, FaTimes } from 'react-icons/fa';
import CustomDialog from '../../../components/CustomDialog';
import logo555Mix from "../../../assets/icons/555mix-logo.avif";
import logo909 from "../../../assets/icons/909.png";

const BookmakerSelector = ({ options, activeId, onSelect }) => {
  if (!options?.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {options.map((bookmaker) => (
        <button
          key={bookmaker.id}
          type="button"
          onClick={() => onSelect(bookmaker.id)}
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${
            activeId === bookmaker.id
              ? 'bg-customAccent text-white dark:text-customFontMilkWhite'
              : 'bg-customLightMilkWhite text-customSecondaryText dark:bg-custom_dark_background dark:text-customFontMilkWhite/70'
          }`}
        >
          {bookmaker.bookmaker}
        </button>
      ))}
    </div>
  );
};

const MarketRows = ({ title, rows, type }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[12px] font-bold uppercase text-customRiver dark:text-customFontMilkWhite/70">{title}</span>
    {rows?.length ? (
      rows.map((line, index) => (
        <div key={`${type}-${index}`} className="text-xs font-medium text-customSecondaryText dark:text-customFontMilkWhite/80">
          {type === 'asian_handicap' ? (
            <>
              <span className={`font-semibold ${line.header === '1' ? 'text-customAccentBlue' : 'text-customFontRed'}`}>
                {line.header === '1' ? 'H' : 'A'}
              </span>
              {' | '}
              <span className="font-semibold text-customSurfaceMuted/90 dark:text-customWhite/80">
                ({line.handicap}) @ {line.odds}
              </span>
              {/* <FaArrowUp className="ml-1 inline text-customFontGreen" /> */}
            </>
          ) : (
            <>
              <span className={`font-semibold ${line.header === 'Over' ? 'text-customDarkGreen' : 'text-customAccentBlue'}`}>
                {line.header === 'Over' ? 'O' : 'U'}
              </span>
              {' | '}
              <span className="font-semibold text-customSurfaceMuted/90 dark:text-customWhite/80">
                {line.name} @ {line.odds}
              </span>
              {/* <FaArrowDown className="ml-1 inline text-customFontRed" /> */}
            </>
          )}
        </div>
      ))
    ) : (
      <span className="text-xs text-customMuted dark:text-customFontMilkWhite/50">N/A</span>
    )}
  </div>
);

const BookmakerDetails = ({ bookmaker }) => {
  if (!bookmaker) {
    return <span className="text-xs text-customMuted dark:text-customFontMilkWhite/50">Select a bookmaker to view details</span>;
  }

  return (
    <div className="rounded-md border border-customTwitch/10 bg-customGray/60 p-2 dark:border-custom_dark_secondary dark:bg-custom_dark_primary">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={bookmaker.bookmaker === "555Mix" ? logo555Mix : logo909} alt="" width={bookmaker.bookmaker === "555Mix" ? 15 : 12} height={'auto'} />
          <span className="text-[10px] font-semibold uppercase text-customSurface dark:text-customWhite">{bookmaker.bookmaker}</span>
        </div>
        <span className="text-[10px] font-semibold text-customSurface dark:text-customMuted">{bookmaker.matchup}</span>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2">
        <MarketRows title="Asian Handicap" rows={bookmaker.asian_handicap} type="asian_handicap" />
        <MarketRows title="Goal Line" rows={bookmaker.goal_line} type="goal_line" />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <span className="flex gap-2 items-center rounded-xl border border-customTwitch/20 bg-white/80 px-3 py-2 shadow-sm dark:border-custom_dark_secondary dark:bg-custom_dark_background">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-customSurfaceMuted dark:text-customFontMilkWhite/55">Body</span>
          <span className="text-xs font-bold text-customPrimaryText dark:text-customFontMilkWhite">{bookmaker.burmese_handicap?.body ?? 'N/A'}</span>
        </span>
        <span className="flex gap-2 items-center rounded-xl border border-customTwitch/20 bg-white/80 px-3 py-2 shadow-sm dark:border-custom_dark_secondary dark:bg-custom_dark_background">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-customSurfaceMuted dark:text-customFontMilkWhite/55">Goal</span>
          <span className="text-xs font-bold text-customPrimaryText dark:text-customFontMilkWhite">{bookmaker.burmese_handicap?.goal ?? 'N/A'}</span>
        </span>
      </div>
    </div>
  );
};

const OddsDetailsDialog = ({ open, onClose, league, kickoff, base = [], reference = [] }) => {
  const [activeBaseId, setActiveBaseId] = useState(base?.[0]?.id ?? null);
  const [activeReferenceId, setActiveReferenceId] = useState(reference?.[0]?.id ?? null);

  useEffect(() => {
    if (open) {
      setActiveBaseId(base?.[0]?.id ?? null);
      setActiveReferenceId(reference?.[0]?.id ?? null);
    }
  }, [open, base, reference]);

  const activeBase = useMemo(() => base.find((item) => item.id === activeBaseId) ?? base[0], [base, activeBaseId]);
  const activeReference = useMemo(
    () => reference.find((item) => item.id === activeReferenceId) ?? reference[0],
    [reference, activeReferenceId]
  );

  return (
    <CustomDialog
      open={open}
      width={400}
      padding={2}
      isMobile={false}
      extraStyle={{ maxHeight: '85vh', overflowY: 'auto', borderRadius: 5 }}
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between border-b border-customLightMilkWhite pb-2 dark:border-custom_dark_secondary">
          <div className="flex flex-col">
            <span id="odds-details-title" className="text-base font-semibold text-customPrimaryText dark:text-customFontMilkWhite">
              Odds Hub Details
            </span>
            <span className="text-xs text-customSecondaryText dark:text-customMuted">
              {league} | {kickoff}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-customSecondaryText transition-colors hover:bg-customLightMilkWhite dark:text-customFontMilkWhite dark:hover:bg-custom_dark_background"
            aria-label="Close details dialog"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-3" id="odds-details-description">
          <div className="flex flex-col gap-2 rounded-md border border-customLightMilkWhite bg-white p-2 shadow-lg dark:border-custom_dark_secondary dark:bg-custom_dark_primary2">
            {/* <span className="text-sm font-semibold text-customPrimaryText dark:text-customFontMilkWhite">Base Odds</span> */}
            {/* <BookmakerSelector options={base} activeId={activeBaseId} onSelect={setActiveBaseId} /> */}
            <BookmakerDetails bookmaker={activeBase} />
          </div>

          <div className="flex flex-col gap-2 rounded-md border border-customLightMilkWhite bg-white p-2 shadow-lg dark:border-custom_dark_secondary dark:bg-custom_dark_primary2">
            {/* <span className="text-sm font-semibold text-customPrimaryText dark:text-customFontMilkWhite">Reference Odds</span> */}
            {/* <BookmakerSelector options={reference} activeId={activeReferenceId} onSelect={setActiveReferenceId} /> */}
            <BookmakerDetails bookmaker={activeReference} />
          </div>
        </div>
      </div>
    </CustomDialog>
  );
};

export default OddsDetailsDialog;
